/**
 * CyberSells Pixel Companion
 * A sprite-based avatar overlay that provides recommendations and interacts with users
 */

(function() {
  'use strict';

  const CONFIG = {
    spriteWidth: 48,
    spriteHeight: 48,
    frameRate: 8,
    animationStates: {
      idle: { frames: 4, row: 0 },
      walk: { frames: 4, row: 1 },
      point: { frames: 2, row: 2 },
      emote: { frames: 3, row: 3 }
    },
    maxBubblesPerSession: 3,
    bubbleCooldown: 30000,
    bubbleDisplayTime: 8000,
    movementSpeed: 2,
    defaultPosition: { x: window.innerWidth - 100, y: window.innerHeight - 100 },
    boundaryPadding: 20,
    messages: {
      welcome: ["[SYSTEM] Greetings, player!", "[AI] Ready to level up?", "[SYSTEM] Welcome back!", "[AI] Cyber-greetings!"],
      recommendation: ["[ALERT] Synergy detected: {title}", "[QUEST] Side quest available -> {title}", "[ITEM] Power-up found: {title}"],
      equipped: ["[SUCCESS] Item equipped!", "[INVENTORY] Added to inventory!", "[STATS] Stats updated!", "[AI] Nice choice!"]
    }
  };

  class PixelCompanion {
    constructor(container, settings = {}) {
      this.container = container;
      this.settings = {
        enabled: settings.enabled !== false,
        position: settings.position || 'bottom-right',
        quietMode: settings.quietMode || false,
        reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches
      };
      
      this.state = this.loadState();
      this.currentAnimation = 'idle';
      this.currentFrame = 0;
      this.position = this.state.position || CONFIG.defaultPosition;
      this.targetPosition = null;
      this.bubbleQueue = [];
      this.lastBubbleTime = this.state.lastBubbleTime || 0;
      this.bubblesShownThisSession = this.state.bubblesShownThisSession || 0;
      this.seenRecommendations = this.state.seenRecommendations || [];
      this.elements = {};
      this.animationFrame = null;
      this.lastFrameTime = 0;
      
      this.init();
    }

    init() {
      if (!this.settings.enabled || this.state.dismissed) return;
      
      // Only exclude from checkout/thank you pages
      if (window.location.pathname.includes('/checkout') || 
          window.location.pathname.includes('/thank')) {
        return;
      }

      this.createElements();
      this.attachEventListeners();
      this.startAnimationLoop();
      
      setTimeout(() => this.showBubble(this.randomMessage('welcome')), 1000);
      this.checkRecommendations();
    }

    createElements() {
      this.container.innerHTML = `
        <div class="pixel-companion-avatar" data-companion-avatar>
          <div class="pixel-companion-sprite"></div>
          <div class="pixel-companion-arrow" style="display: none;">
            <svg width="24" height="24" viewBox="0 0 24 24">
              <path d="M12 2L12 20M12 20L6 14M12 20L18 14" stroke="currentColor" stroke-width="2" fill="none"/>
            </svg>
          </div>
        </div>
        <div class="pixel-companion-bubble" style="display: none;">
          <div class="pixel-companion-bubble-content"></div>
          <div class="pixel-companion-bubble-tail"></div>
        </div>
        <button class="pixel-companion-toggle" title="Toggle Companion">
          <span class="pixel-companion-toggle-icon">??</span>
        </button>
      `;

      this.elements.avatar = this.container.querySelector('[data-companion-avatar]');
      this.elements.sprite = this.container.querySelector('.pixel-companion-sprite');
      this.elements.arrow = this.container.querySelector('.pixel-companion-arrow');
      this.elements.bubble = this.container.querySelector('.pixel-companion-bubble');
      this.elements.bubbleContent = this.container.querySelector('.pixel-companion-bubble-content');
      this.elements.toggle = this.container.querySelector('.pixel-companion-toggle');

      this.updateAvatarPosition();
    }

    attachEventListeners() {
      this.elements.toggle.addEventListener('click', () => this.toggleVisibility());

      if (!this.settings.reducedMotion) {
        document.addEventListener('click', (e) => {
          if (!this.container.contains(e.target)) {
            this.moveToward({ x: e.clientX, y: e.clientY });
          }
        });
      }

      document.addEventListener('product:added-to-cart', () => {
        this.playAnimation('emote');
        this.showBubble(this.randomMessage('equipped'));
        this.pointAtCart();
      });

      window.addEventListener('resize', () => {
        this.constrainPosition();
        this.updateAvatarPosition();
      });
    }

    startAnimationLoop() {
      const animate = (timestamp) => {
        if (!this.lastFrameTime) this.lastFrameTime = timestamp;
        const elapsed = timestamp - this.lastFrameTime;

        if (elapsed >= 1000 / CONFIG.frameRate) {
          this.updateFrame();
          this.updateMovement();
          this.lastFrameTime = timestamp;
        }

        this.animationFrame = requestAnimationFrame(animate);
      };

      this.animationFrame = requestAnimationFrame(animate);
    }

    updateFrame() {
      if (this.settings.reducedMotion && this.currentAnimation === 'walk') return;

      const animation = CONFIG.animationStates[this.currentAnimation];
      this.currentFrame = (this.currentFrame + 1) % animation.frames;

      const x = this.currentFrame * CONFIG.spriteWidth;
      const y = animation.row * CONFIG.spriteHeight;
      this.elements.sprite.style.backgroundPosition = `-${x}px -${y}px`;
    }

    updateMovement() {
      if (!this.targetPosition || this.settings.reducedMotion) return;

      const dx = this.targetPosition.x - this.position.x;
      const dy = this.targetPosition.y - this.position.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < 5) {
        this.targetPosition = null;
        this.playAnimation('idle');
        return;
      }

      const speed = CONFIG.movementSpeed;
      this.position.x += (dx / distance) * speed;
      this.position.y += (dy / distance) * speed;

      this.constrainPosition();
      this.updateAvatarPosition();
    }

    playAnimation(name) {
      if (this.currentAnimation === name) return;
      this.currentAnimation = name;
      this.currentFrame = 0;
    }

    moveToward(target) {
      this.targetPosition = target;
      this.playAnimation('walk');
    }

    updateAvatarPosition() {
      this.elements.avatar.style.transform = `translate(${this.position.x}px, ${this.position.y}px)`;
    }

    constrainPosition() {
      const padding = CONFIG.boundaryPadding;
      this.position.x = Math.max(padding, Math.min(window.innerWidth - CONFIG.spriteWidth - padding, this.position.x));
      this.position.y = Math.max(padding, Math.min(window.innerHeight - CONFIG.spriteHeight - padding, this.position.y));
    }

    showBubble(message, link = null) {
      if (this.bubblesShownThisSession >= CONFIG.maxBubblesPerSession) return;
      if (Date.now() - this.lastBubbleTime < CONFIG.bubbleCooldown) return;
      if (this.settings.quietMode) return;

      this.elements.bubbleContent.textContent = message;
      
      if (link) {
        const linkEl = document.createElement('a');
        linkEl.href = link.url;
        linkEl.textContent = 'View';
        linkEl.className = 'pixel-companion-bubble-link';
        this.elements.bubbleContent.appendChild(document.createElement('br'));
        this.elements.bubbleContent.appendChild(linkEl);
      }

      this.elements.bubble.style.display = 'block';
      this.positionBubble();

      this.bubblesShownThisSession++;
      this.lastBubbleTime = Date.now();

      setTimeout(() => {
        this.elements.bubble.style.display = 'none';
      }, CONFIG.bubbleDisplayTime);

      this.saveState();
    }

    positionBubble() {
      const bubbleRect = this.elements.bubble.getBoundingClientRect();
      let left = this.position.x - bubbleRect.width / 2 + CONFIG.spriteWidth / 2;
      let top = this.position.y - bubbleRect.height - 10;
      left = Math.max(10, Math.min(window.innerWidth - bubbleRect.width - 10, left));
      top = Math.max(10, top);
      this.elements.bubble.style.left = left + 'px';
      this.elements.bubble.style.top = top + 'px';
    }

    pointAtElement(selector) {
      const element = document.querySelector(selector);
      if (!element) return;

      const rect = element.getBoundingClientRect();
      const target = {
        x: rect.left + rect.width / 2 - CONFIG.spriteWidth / 2,
        y: rect.top + rect.height / 2 - CONFIG.spriteHeight / 2
      };

      this.moveToward(target);
      this.playAnimation('point');
      
      this.elements.arrow.style.display = 'block';
      setTimeout(() => {
        this.elements.arrow.style.display = 'none';
      }, 3000);
    }

    pointAtCart() {
      const selectors = ['#cart-icon-bubble', '.header__icon--cart', 'a[href="/cart"]', '[data-cart-icon]'];
      for (const selector of selectors) {
        if (document.querySelector(selector)) {
          this.pointAtElement(selector);
          return;
        }
      }
    }

    async checkRecommendations() {
      await new Promise(resolve => setTimeout(resolve, 3000));
      const pageType = this.detectPageType();
      let recommendations = [];

      switch (pageType) {
        case 'product': recommendations = await this.getProductRecommendations(); break;
        case 'collection': recommendations = await this.getCollectionRecommendations(); break;
        case 'cart': recommendations = await this.getCartRecommendations(); break;
      }

      if (recommendations.length > 0) {
        this.showRecommendation(recommendations[0]);
      }
    }

    detectPageType() {
      const path = window.location.pathname;
      if (path.includes('/products/')) return 'product';
      if (path.includes('/collections/')) return 'collection';
      if (path.includes('/cart')) return 'cart';
      return 'other';
    }

    async getProductRecommendations() {
      const productId = window.ShopifyAnalytics?.meta?.product?.id;
      if (!productId) return [];

      try {
        const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=3`);
        const data = await response.json();
        return data.products || [];
      } catch (error) {
        return this.getFallbackRecommendations();
      }
    }

    getFallbackRecommendations() {
      const productData = window.__COMPANION_PRODUCT_DATA__;
      if (!productData || !productData.relatedProducts) return [];
      return productData.relatedProducts.slice(0, 3);
    }

    async getCollectionRecommendations() {
      const collectionData = window.__COMPANION_COLLECTION_DATA__;
      if (!collectionData || !collectionData.products) return [];
      return collectionData.products.slice(0, 3);
    }

    async getCartRecommendations() {
      const cartData = window.__COMPANION_CART_DATA__;
      if (!cartData || !cartData.recommendations) return [];
      return cartData.recommendations.slice(0, 3);
    }

    showRecommendation(product) {
      if (this.seenRecommendations.includes(product.handle)) return;

      const template = this.randomMessage('recommendation');
      const message = template.replace('{title}', product.title);
      
      this.showBubble(message, { url: product.url });
      this.seenRecommendations.push(product.handle);
      this.saveState();

      const productCard = document.querySelector(`[data-product-handle="${product.handle}"]`);
      if (productCard) {
        setTimeout(() => {
          this.pointAtElement(`[data-product-handle="${product.handle}"]`);
        }, 2000);
      }
    }

    randomMessage(category) {
      const messages = CONFIG.messages[category];
      return messages[Math.floor(Math.random() * messages.length)];
    }

    toggleVisibility() {
      this.state.dismissed = !this.state.dismissed;
      
      if (this.state.dismissed) {
        // Hide avatar and bubble, but keep toggle visible
        this.elements.avatar.style.display = 'none';
        this.elements.bubble.style.display = 'none';
        this.elements.toggle.innerHTML = '<span class="pixel-companion-toggle-icon">??</span>';
        this.elements.toggle.title = 'Show Companion';
      } else {
        // Show everything
        this.elements.avatar.style.display = 'block';
        this.elements.toggle.innerHTML = '<span class="pixel-companion-toggle-icon">?</span>';
        this.elements.toggle.title = 'Hide Companion';
      }

      this.saveState();
      
      // Dispatch event for console checkbox sync
      document.dispatchEvent(new CustomEvent('companion:toggled', {
        detail: { dismissed: this.state.dismissed }
      }));
    }

    loadState() {
      try {
        const saved = localStorage.getItem('pixelCompanion');
        return saved ? JSON.parse(saved) : {};
      } catch (error) {
        return {};
      }
    }

    saveState() {
      try {
        localStorage.setItem('pixelCompanion', JSON.stringify({
          position: this.position,
          dismissed: this.state.dismissed,
          lastBubbleTime: this.lastBubbleTime,
          bubblesShownThisSession: this.bubblesShownThisSession,
          seenRecommendations: this.seenRecommendations
        }));
      } catch (error) {
        console.error('[Companion] Failed to save state:', error);
      }
    }
  }

  function init() {
    const container = document.querySelector('[data-pixel-companion]');
    if (!container) return;

    const settings = {
      enabled: container.dataset.enabled !== 'false',
      position: container.dataset.position || 'bottom-right',
      quietMode: container.dataset.quietMode === 'true'
    };

    window.pixelCompanion = new PixelCompanion(container, settings);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
